import type { FccPassengerAuthenticationState } from '@backoffice-frontend/graphql'
import type {
  GetPassengersDeliveryActionFragment,
  GetPassengersPickupActionFragment,
  GetPassengersWaypointFragment,
} from './passenger-filters.hook'

export type WaypointAction = GetPassengersWaypointFragment['actions'][number]
export type Trip = NonNullable<GetPassengersDeliveryActionFragment['trip']>
export type Customer = GetPassengersDeliveryActionFragment['customer']

export type RidePassengerInfo =
  GetPassengersDeliveryActionFragment['ridePassengerInfo']

export type PassengerInfo = {
  trip: Trip
  fccStop: GetPassengersWaypointFragment['fccStop'] | null
  stopServiceAreaId: string | null
  customer: Customer | null
  ridePassengerInfo: RidePassengerInfo | null
  authenticationState: FccPassengerAuthenticationState | null
  notShownUpAt: Date | null
  pickedUpAt: Date | null
  eta: Date
  tripIsCancelled?: boolean
  nextWaypointActionType: 'PICKUP' | 'DELIVERY'
}

const isPickupAction = (
  action: WaypointAction,
): action is GetPassengersPickupActionFragment & { trip: Trip } => {
  return action.__typename === 'FccWaypointPickupAction' && action.trip !== null
}

const isDeliveryAction = (
  action: WaypointAction,
): action is GetPassengersDeliveryActionFragment & { trip: Trip } => {
  return (
    action.__typename === 'FccWaypointDeliveryAction' && action.trip !== null
  )
}

const getPassengerInfoFromAction = (
  action: (
    | GetPassengersPickupActionFragment
    | GetPassengersDeliveryActionFragment
  ) & { trip: Trip },
  fccStop: GetPassengersWaypointFragment['fccStop'] | null = null,
  stopServiceAreaId: string | null = null,
): PassengerInfo => {
  const baseInfo = {
    trip: action.trip,
    fccStop,
    stopServiceAreaId,
    customer: action.customer,
    ridePassengerInfo: action.ridePassengerInfo ?? null,
    eta: new Date(action.estimatedAt),
    pickedUpAt: action.pickedUpAt ? new Date(action.pickedUpAt) : null,
    notShownUpAt:
      isPickupAction(action) && action.notShownUpAt
        ? new Date(action.notShownUpAt)
        : null,
    authenticationState: isPickupAction(action)
      ? action.adPassengerAuthenticationState
      : null,
  }

  // Only include tripIsCancelled for pickup actions
  if (isPickupAction(action)) {
    return {
      ...baseInfo,
      tripIsCancelled: action.tripIsCancelled ?? false,
      nextWaypointActionType: 'PICKUP',
    }
  }

  return {
    ...baseInfo,
    nextWaypointActionType: 'DELIVERY',
  }
}

export const getOnBoardPassengers = (
  upcomingWaypoints: GetPassengersWaypointFragment[],
) => {
  const upcomingActionsWithFccStop = upcomingWaypoints.flatMap(waypoint =>
    waypoint.actions.map(action => ({
      action,
      fccStop: waypoint.fccStop,
      stopServiceAreaId: waypoint.stopWithServiceArea?.serviceArea.id ?? null,
    })),
  )

  const upcomingPickups = upcomingActionsWithFccStop
    .map(({ action }) => action)
    .filter(isPickupAction)

  const upcomingDeliveriesWithFccStop = upcomingActionsWithFccStop.filter(
    (
      item,
    ): item is {
      action: GetPassengersDeliveryActionFragment & { trip: Trip }
      fccStop: GetPassengersWaypointFragment['fccStop'] | null
      stopServiceAreaId: string | null
    } => isDeliveryAction(item.action),
  )

  const passengersOnBoard: PassengerInfo[] = []

  upcomingDeliveriesWithFccStop.forEach(
    ({ action: upcomingDelivery, fccStop, stopServiceAreaId }) => {
      const matchingPickup = upcomingPickups.find(
        upcomingPickup => upcomingPickup.trip?.id === upcomingDelivery.trip.id,
      )

      // When the upcoming delivery has no matching upcoming pickup, we infer that
      // the customer is currently on board.
      if (!matchingPickup) {
        passengersOnBoard.push(
          getPassengerInfoFromAction(
            upcomingDelivery,
            fccStop,
            stopServiceAreaId,
          ),
        )
      }
    },
  )

  return passengersOnBoard
}

export const getBoardingPassengers = (
  waypoint: GetPassengersWaypointFragment,
) => {
  const boardingPassengers: PassengerInfo[] = []

  waypoint.actions.forEach(action => {
    if (isPickupAction(action) && action.trip?.id) {
      boardingPassengers.push(
        getPassengerInfoFromAction(
          action,
          waypoint.fccStop,
          waypoint.stopWithServiceArea?.serviceArea.id ?? null,
        ),
      )
    }
  })

  boardingPassengers.sort((a, b) => a.eta.getTime() - b.eta.getTime())

  return boardingPassengers
}

export const getDeboardingPassengers = (
  waypoint: GetPassengersWaypointFragment,
) => {
  const deboardingPassengers: PassengerInfo[] = []

  waypoint.actions.forEach(action => {
    if (isDeliveryAction(action) && action.trip?.id) {
      deboardingPassengers.push(
        getPassengerInfoFromAction(
          action,
          waypoint.fccStop,
          waypoint.stopWithServiceArea?.serviceArea.id ?? null,
        ),
      )
    }
  })

  deboardingPassengers.sort((a, b) => a.eta.getTime() - b.eta.getTime())

  return deboardingPassengers
}

export const getStayingPassengers = (
  waypoint: GetPassengersWaypointFragment,
  futureWaypoints: GetPassengersWaypointFragment[],
) => {
  const boardingPassengers = getBoardingPassengers(waypoint)
  const futurePassengersOnBoard = getOnBoardPassengers(futureWaypoints)

  const stayingPassengers = futurePassengersOnBoard.filter(futurePassenger =>
    boardingPassengers.every(
      boardingPassenger =>
        boardingPassenger.trip.id !== futurePassenger.trip.id,
    ),
  )

  stayingPassengers.sort((a, b) => a.eta.getTime() - b.eta.getTime())

  return stayingPassengers
}

export const getAllPassengers = (
  waypoints: GetPassengersWaypointFragment[],
) => {
  const passengers: PassengerInfo[] = []

  waypoints.forEach(waypoint => {
    waypoint.actions.forEach(action => {
      if (isPickupAction(action) || isDeliveryAction(action)) {
        if (
          !passengers.some(passenger => passenger.trip.id === action.trip.id)
        ) {
          passengers.push(
            getPassengerInfoFromAction(
              action,
              waypoint.fccStop,
              waypoint.stopWithServiceArea?.serviceArea.id ?? null,
            ),
          )
        }
      }
    })
  })

  return passengers
}

export const getPassengerCount = (passengers: PassengerInfo[]) =>
  passengers.reduce(
    (acc, cur) =>
      acc + (cur.ridePassengerInfo?.boardingRequirements?.headCount ?? 0),
    0,
  )
